export type Severity = 'normal' | 'caution' | 'critical';

export interface Interpretation {
  value: number;
  severity: Severity;
  label: string;
  detail?: string;
}

/** Shock index = HR ÷ SBP. */
export function shockIndex(heartRate: number, systolic: number): Interpretation {
  const value = heartRate / systolic;
  if (value >= 1.0) {
    return {
      value,
      severity: 'critical',
      label: 'Elevated',
      detail: 'SI ≥ 1.0 is associated with occult shock, need for transfusion, and increased mortality.',
    };
  }
  if (value >= 0.9) {
    return {
      value,
      severity: 'caution',
      label: 'Borderline',
      detail: 'SI 0.9–1.0 may indicate early or compensated shock. Reassess frequently.',
    };
  }
  return { value, severity: 'normal', label: 'Within expected range', detail: 'Typical adult range is 0.5–0.7.' };
}

/** Modified shock index = HR ÷ MAP (normal ≈ 0.7–1.3). */
export function modifiedShockIndex(heartRate: number, map: number): Interpretation {
  const value = heartRate / map;
  if (value > 1.3) {
    return { value, severity: 'critical', label: 'Elevated', detail: 'MSI > 1.3 suggests inadequate stroke volume and higher mortality.' };
  }
  if (value < 0.7) {
    return { value, severity: 'caution', label: 'Low', detail: 'MSI < 0.7 may reflect hyperdynamic state or high afterload.' };
  }
  return { value, severity: 'normal', label: 'Within expected range', detail: 'Normal MSI ≈ 0.7–1.3.' };
}

/**
 * Shock Index, Pediatric Age-adjusted (SIPA) thresholds for ages 4–16 years
 * (Acker et al., J Pediatr Surg 2015). Not validated under 4 years.
 */
export function pediatricShockIndexThreshold(ageYears: number): number | null {
  if (ageYears < 4 || ageYears > 16) return null;
  if (ageYears <= 6) return 1.22;
  if (ageYears <= 12) return 1.0;
  return 0.9;
}

export function pediatricShockIndex(heartRate: number, systolic: number, ageYears: number): Interpretation {
  const value = heartRate / systolic;
  const threshold = pediatricShockIndexThreshold(ageYears);
  if (threshold === null) {
    return {
      value,
      severity: 'caution',
      label: 'Not validated for this age',
      detail: 'SIPA is validated for ages 4–16. Use age-based vital sign norms and clinical assessment.',
    };
  }
  return value > threshold
    ? { value, severity: 'critical', label: 'Elevated (SIPA)', detail: `Above age-adjusted threshold of ${threshold}.` }
    : { value, severity: 'normal', label: 'Within SIPA threshold', detail: `Age-adjusted threshold: ${threshold}.` };
}

/** Mean arterial pressure = (SBP + 2 × DBP) ÷ 3. */
export function meanArterialPressure(systolic: number, diastolic: number): number {
  return (systolic + 2 * diastolic) / 3;
}

export function interpretMap(map: number): Interpretation {
  if (map < 60) {
    return { value: map, severity: 'critical', label: 'Critically low', detail: 'MAP < 60 mmHg risks inadequate organ perfusion.' };
  }
  if (map < 65) {
    return { value: map, severity: 'caution', label: 'Below common resuscitation target', detail: 'Septic shock target is typically MAP ≥ 65 mmHg.' };
  }
  if (map > 110) {
    return { value: map, severity: 'caution', label: 'Elevated', detail: 'Consider hypertensive emergency if accompanied by end-organ symptoms.' };
  }
  return { value: map, severity: 'normal', label: 'Within expected range', detail: 'Typical adult MAP is 70–100 mmHg.' };
}

export interface VitalRange {
  low: number;
  high: number;
}

export interface PediatricVitalNorms {
  band: string;
  heartRateAwake: VitalRange;
  heartRateAsleep: VitalRange;
  respiratoryRate: VitalRange;
  systolic: VitalRange;
  hypotensionSystolic: number;
}

interface NormBand extends Omit<PediatricVitalNorms, 'hypotensionSystolic'> {
  maxAgeYears: number;
}

/** PALS (2020) reference ranges. */
const PEDIATRIC_NORMS: NormBand[] = [
  { band: 'Neonate (< 28 days)', maxAgeYears: 28 / 365, heartRateAwake: { low: 100, high: 205 }, heartRateAsleep: { low: 90, high: 160 }, respiratoryRate: { low: 30, high: 53 }, systolic: { low: 67, high: 84 } },
  { band: 'Infant (1–12 months)', maxAgeYears: 1, heartRateAwake: { low: 100, high: 180 }, heartRateAsleep: { low: 90, high: 160 }, respiratoryRate: { low: 30, high: 53 }, systolic: { low: 72, high: 104 } },
  { band: 'Toddler (1–2 years)', maxAgeYears: 3, heartRateAwake: { low: 98, high: 140 }, heartRateAsleep: { low: 80, high: 120 }, respiratoryRate: { low: 22, high: 37 }, systolic: { low: 86, high: 106 } },
  { band: 'Preschool (3–5 years)', maxAgeYears: 6, heartRateAwake: { low: 80, high: 120 }, heartRateAsleep: { low: 65, high: 100 }, respiratoryRate: { low: 20, high: 28 }, systolic: { low: 89, high: 112 } },
  { band: 'School age (6–11 years)', maxAgeYears: 12, heartRateAwake: { low: 75, high: 118 }, heartRateAsleep: { low: 58, high: 90 }, respiratoryRate: { low: 18, high: 25 }, systolic: { low: 97, high: 115 } },
  { band: 'Adolescent (12–15 years)', maxAgeYears: 16, heartRateAwake: { low: 60, high: 100 }, heartRateAsleep: { low: 50, high: 90 }, respiratoryRate: { low: 12, high: 20 }, systolic: { low: 110, high: 131 } },
];

/** Systolic hypotension threshold (PALS): SBP below this value is hypotensive. */
export function pediatricHypotensionThreshold(ageYears: number): number {
  if (ageYears < 28 / 365) return 60;
  if (ageYears < 1) return 70;
  if (ageYears <= 10) return 70 + 2 * Math.floor(ageYears);
  return 90;
}

export function pediatricVitalNorms(ageYears: number): PediatricVitalNorms | null {
  const band = PEDIATRIC_NORMS.find((b) => ageYears < b.maxAgeYears);
  if (!band) return null;
  const { maxAgeYears: _max, ...norms } = band;
  return { ...norms, hypotensionSystolic: pediatricHypotensionThreshold(ageYears) };
}

export const PEDIATRIC_VITAL_TABLE = PEDIATRIC_NORMS.map(({ maxAgeYears: _m, ...rest }) => rest);
